type HasRowFlag = { is_opportunity?: boolean } | null | undefined;
type HasTypeFlag = { is_opportunity?: boolean } | null | undefined;

/**
 * Whether a visit counts as an Opportunity: its own row flag OR its appointment type's,
 * which is what the backend already does everywhere that filters or counts
 * (`views/appointment.py`, `coach/services/opportunity_eligibility.py`).
 *
 * The row half is gated by the feature flag on purpose. A CounselEAR import writes
 * `Appointment.is_opportunity` directly, so honouring it un-gated would silently turn those
 * imported visits into Opportunities for practices that never asked for the feature.
 */
export const resolveIsOpportunity = (
  appointment: HasRowFlag,
  appointmentType: HasTypeFlag,
  rowFlagEnabled: boolean
): boolean =>
  Boolean(
    (rowFlagEnabled && appointment?.is_opportunity === true) ||
      appointmentType?.is_opportunity === true
  );

/** True only when the row flag is the ONLY reason this visit is an opportunity. */
export const isManuallyFlagged = (
  appointment: HasRowFlag,
  appointmentType: HasTypeFlag,
  rowFlagEnabled: boolean
): boolean =>
  Boolean(
    rowFlagEnabled &&
      appointment?.is_opportunity === true &&
      appointmentType?.is_opportunity !== true
  );

/**
 * Whether the appointment form shows the Opportunity controls at all.
 *
 * `hidePatientField` is the new-patient wizard: a patient id is already present there, so the
 * card would state "No hearing aids on file" about a patient created seconds ago.
 */
export const showOpportunityFields = (
  flagEnabled: boolean | undefined,
  hidePatientField: boolean | undefined
): boolean => Boolean(flagEnabled) && !hidePatientField;

/**
 * Appends the top-level `is_opportunity` token to a RestQL query. Call it only when the LD flag
 * is on, so a flag-off GET stays byte-identical to the pre-feature one.
 *
 * Deliberately not shaped as the `missingTokens.filter(query.includes)` check
 * `withConsultSignals` uses: every appointments query already carries
 * `type{...,is_opportunity}`, so a substring test is true even when the TOP-LEVEL token is
 * absent. Anchoring at the head of the query answers the only question that matters and keeps
 * the helper idempotent.
 */
export const withOpportunity = (query: string): string =>
  query.startsWith("{is_opportunity,")
    ? query
    : query.replace(/^\{/, "{is_opportunity,");
